import { z } from 'zod';
import {
  customValuesInputSchema,
  type CustomFieldType,
  type CustomValues,
  type LeadTagDto,
} from './customization';
import type { LeadAttention } from './attention';
import { DocumentType, InterestLevel, LeadStatus, PipelineStage, ContactState } from '../enums';
import { consentInputSchema, leadAttributionSchema, purchaseProfileSchema } from './commercial';
import type { IdentityMatch } from './contacts';
import { nextTaskFields } from './interactions';
import { BULK_ACTION_MAX_LEADS, EXPORT_MAX_ROWS } from '../constants';
import { cursorPaginationSchema, dateBoundSchema, optionalPatchText } from './common';

const FollowupStateValues = ['INACTIVO', 'NO_GESTIONADO', 'AL_DIA', 'VENCIDO'] as const;
const OverdueBandValues = ['HOY', 'AYER', 'DIAS_2_7', 'MAS_7'] as const;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

export const ACTIONS_FILTER_VALUES = ['0', '1', '2', '3-5', '6+'] as const;
export type ActionsFilter = (typeof ACTIONS_FILTER_VALUES)[number];
export const ACTIONS_FILTER_LABELS: Record<ActionsFilter, string> = {
  '0': 'Sin gestiones',
  '1': '1 gestión',
  '2': '2 gestiones',
  '3-5': '3 a 5 gestiones',
  '6+': '6 gestiones o más',
};
/** Rango [min, max] de gestiones que representa cada opción (max null = sin tope). */
export function actionsRange(value: ActionsFilter): { min: number; max: number | null } {
  switch (value) {
    case '0':
      return { min: 0, max: 0 };
    case '1':
      return { min: 1, max: 1 };
    case '2':
      return { min: 2, max: 2 };
    case '3-5':
      return { min: 3, max: 5 };
    default:
      return { min: 6, max: null };
  }
}

export const leadFiltersSchema = cursorPaginationSchema.extend({
  companyId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  advisorId: z.string().uuid().optional(),
  /** 'SIN_ASIGNAR' = sin asesor */
  unassigned: z.coerce.boolean().optional(),
  followupState: z.enum(FollowupStateValues).optional(),
  overdueBand: z.enum(OverdueBandValues).optional(),
  /** Días de atraso exactos (calendario de Lima): 1 a 6, o 7 = siete o más. Implica seguimiento VENCIDO. */
  overdueDays: z.coerce.number().int().min(1).max(7).optional(),
  /** Cantidad de gestiones completadas por personas (7.21): 0, 1, 2, 3-5 o 6+. */
  actions: z.enum(ACTIONS_FILTER_VALUES).optional(),
  /** Contacto del lead (7.26): sin contacto, no efectivo o efectivo. */
  contactState: z.nativeEnum(ContactState).optional(),
  pipelineStage: z.nativeEnum(PipelineStage).optional(),
  status: z.nativeEnum(LeadStatus).optional(),
  subStatus: z.string().optional(),
  mediumCategory: z.string().optional(),
  q: z.string().trim().min(2).max(100).optional(),
  /** Fecha pura (día completo en la zona de la empresa, «hasta» inclusivo) o instante ISO. */
  capturedFrom: dateBoundSchema.optional(),
  capturedTo: dateBoundSchema.optional(),
  /** Leads con alguna de estas etiquetas (en la URL: `tagIds=a,b`). */
  tagIds: z
    .union([z.array(z.string().uuid()), z.string()])
    .transform((v) =>
      Array.isArray(v)
        ? v
        : v
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
    )
    .pipe(z.array(z.string().uuid()).max(20))
    .optional(),
  /** Igualdad por campo personalizado (`{ clave: valor }`; en la URL: `cf_<clave>=valor`, ver liftCustomFilters). */
  custom: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  sort: z
    .enum([
      'captured_desc',
      'captured_asc',
      'next_task_asc',
      'reference_asc',
      'stage_changed_desc',
      'recontact_desc',
    ])
    .default('captured_desc'),
});
export type LeadFilters = z.infer<typeof leadFiltersSchema>;

/** Opciones del filtro "Días vencidos" (7 = siete o más). */
export const OVERDUE_DAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7] as const;
export function overdueDaysLabel(days: number): string {
  return days >= 7 ? '7 días o más' : `${days} día${days === 1 ? '' : 's'}`;
}

/** Mueve los parámetros `cf_<clave>=valor` (barra de filtros / vistas guardadas) al objeto `custom` que entiende la API. */
export function liftCustomFilters<T extends Record<string, unknown>>(
  query: T,
): Omit<T, `cf_${string}`> & { custom?: Record<string, string> } {
  const out: Record<string, unknown> = {};
  const custom: Record<string, string> = {};
  for (const [k, v] of Object.entries(query)) {
    if (k.startsWith('cf_') && k.length > 3) {
      if (typeof v === 'string' && v !== '') custom[k.slice(3)] = v;
    } else out[k] = v;
  }
  if (Object.keys(custom).length)
    out.custom = { ...((out.custom as Record<string, string>) ?? {}), ...custom };
  return out as Omit<T, `cf_${string}`> & { custom?: Record<string, string> };
}

/** Exportación a Excel (coordinadores, jefes, gerentes y administradores): la selección o el filtro actual. */
export const exportLeadsSchema = z
  .object({
    leadIds: z.array(z.string().uuid()).max(EXPORT_MAX_ROWS).optional(),
    filters: leadFiltersSchema.omit({ cursor: true, limit: true }).optional(),
    /** Tope de filas devueltas (por defecto y máximo `EXPORT_MAX_ROWS` = 5 000). */
    max: z.coerce.number().int().min(1).max(EXPORT_MAX_ROWS).default(EXPORT_MAX_ROWS),
  })
  .refine((v) => !!v.leadIds?.length || !!v.filters, 'Indica los leads o los filtros a exportar');
export type ExportLeadsInput = z.infer<typeof exportLeadsSchema>;
export type ExportLeadsPayload = z.input<typeof exportLeadsSchema>;

export interface LeadExportRow {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  document: string | null;
  district: string | null;
  company: string;
  project: string;
  stage: string;
  subStatus: string | null;
  followup: string;
  advisor: string | null;
  /** Atribución oficial (inmutable): medio y categoría de la captación original; es lo que la tabla muestra y filtra. */
  originalMedium: string | null;
  originalMediumCategory: string | null;
  /** Medio y categoría del último contacto (cambian con cada recontacto). */
  medium: string | null;
  mediumCategory: string | null;
  entryChannel: string | null;
  /** Nombre de la campaña (atribución de Meta / Google o utm_campaign). */
  campaign: string | null;
  /** 7.24: UTM y atribución del anuncio (vacíos cuando el lead no viene de un medio digital). */
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  platform: string | null;
  adsetName: string | null;
  adName: string | null;
  formName: string | null;
  sourceUrl: string | null;
  capturedAt: string;
  lastContactAt: string | null;
  gestiones: number;
  /** 7.26 */
  contact: string;
  contactAttempts: number;
  lastNonEffectiveReason: string | null;
  nextTaskAt: string | null;
  interest: string;
  score: number;
  recontact: boolean;
  status: string;
  doNotContact: boolean;
  /** Etiquetas separadas por coma. */
  tags: string | null;
  /** Campos personalizados de la empresa (columnas dinámicas según `LeadExportResponse.customFields`). */
  custom: CustomValues;
}

/** Orden y títulos de las columnas del Excel (la web formatea fechas y valores). */
export const LEAD_EXPORT_COLUMNS: Array<[keyof LeadExportRow, string]> = [
  ['firstName', 'Nombres'],
  ['lastName', 'Apellidos'],
  ['phone', 'Celular'],
  ['email', 'Correo'],
  ['document', 'Documento'],
  ['district', 'Distrito'],
  ['company', 'Empresa'],
  ['project', 'Proyecto'],
  ['stage', 'Etapa'],
  ['subStatus', 'Sub-estado'],
  ['followup', 'Seguimiento'],
  ['advisor', 'Asesor'],
  ['originalMedium', 'Medio original'],
  ['originalMediumCategory', 'Categoría original'],
  ['medium', 'Medio (último contacto)'],
  ['mediumCategory', 'Categoría (último contacto)'],
  ['entryChannel', 'Canal de entrada'],
  ['campaign', 'Campaña'],
  ['utmSource', 'UTM source'],
  ['utmMedium', 'UTM medium'],
  ['utmCampaign', 'UTM campaign'],
  ['utmTerm', 'UTM term'],
  ['utmContent', 'UTM content'],
  ['platform', 'Plataforma del anuncio'],
  ['adsetName', 'Conjunto de anuncios'],
  ['adName', 'Anuncio'],
  ['formName', 'Formulario'],
  ['sourceUrl', 'URL de origen'],
  ['capturedAt', 'Captado el'],
  ['lastContactAt', 'Último contacto'],
  ['gestiones', 'Gestiones'],
  ['contact', 'Contacto'],
  ['contactAttempts', 'Intentos sin éxito'],
  ['lastNonEffectiveReason', 'Motivo del último intento'],
  ['nextTaskAt', 'Próxima tarea'],
  ['interest', 'Interés'],
  ['score', 'Puntaje'],
  ['recontact', 'Recontacto'],
  ['status', 'Estado'],
  ['doNotContact', 'No contactar'],
  ['id', 'ID'],
  ['tags', 'Etiquetas'],
];

export interface LeadExportResponse {
  /** Campos personalizados de las empresas exportadas (columnas adicionales del Excel, en este orden). */
  customFields: Array<{ key: string; label: string; type: CustomFieldType }>;
  rows: LeadExportRow[];
  /** Cuántos leads cumplen el filtro (puede superar las filas devueltas). */
  total: number;
  truncated: boolean;
  exportedAt: string;
}

export const contactInputSchema = z
  .object({
    firstName: z.string().trim().min(2, 'Ingresa el nombre').max(80),
    lastNamePaternal: optionalText(80),
    lastNameMaternal: optionalText(80),
    phone: optionalText(30),
    email: z
      .string()
      .trim()
      .email('Correo inválido')
      .max(120)
      .optional()
      .nullable()
      .or(z.literal(''))
      .transform((v) => (v ? v : null)),
    documentType: z.nativeEnum(DocumentType).optional().nullable(),
    documentNumber: optionalText(20),
    district: optionalText(80),
  })
  .refine((c) => c.phone || c.email || c.documentNumber, {
    message: 'Ingresa al menos teléfono, correo o documento',
    path: ['phone'],
  });
export type ContactInput = z.infer<typeof contactInputSchema>;

export const createLeadSchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid(),
  contact: contactInputSchema,
  medium: optionalText(80),
  entryChannel: optionalText(40),
  utmSource: optionalText(120),
  utmMedium: optionalText(120),
  utmCampaign: optionalText(160),
  utmTerm: optionalText(160),
  utmContent: optionalText(160),
  interestLevel: z.nativeEnum(InterestLevel).optional(),
  advisorId: z.string().uuid().optional().nullable(),
  /** Nota inicial (se guarda como interacción NOTA). */
  note: optionalText(2000),
  /**
   * Si el contacto ya existe con un lead ACTIVO en el mismo proyecto, ¿reactivar ese lead en vez de crear otro?
   * Solo lo decide la ingesta (`origin = INBOUND`); el registro a mano siempre reutiliza (G1: nadie crea un segundo
   * lead activo de la misma persona en el mismo proyecto).
   */
  reuseActiveLead: z.boolean().default(true),
  externalIds: z.record(z.unknown()).optional(),
  /** Fecha de captación real (ingesta / importación histórica). Nunca futura; por defecto ahora. */
  capturedAt: z.coerce
    .date()
    .refine(
      (d) => d.getTime() <= Date.now() + 5 * 60_000,
      'La fecha de captación no puede ser futura',
    )
    .optional(),
  /** Atribución estructurada (ids de campaña / anuncio / formulario, URL de origen). */
  attribution: leadAttributionSchema.partial().optional(),
  /** Consentimiento de contacto declarado al captar (formulario, WhatsApp, sala de ventas). */
  consent: consentInputSchema.optional(),
  profile: purchaseProfileSchema.optional(),
  /** Campos personalizados de la empresa (`{ clave: valor }`), validados contra su definición. */
  custom: customValuesInputSchema.optional(),
  /** Etiquetas por id o por nombre (deben existir en la empresa). */
  tags: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
  /**
   * G1: el registro a mano de una persona que ya existe exige confirmarlo (409 `DUPLICATE_CONFIRM` si falta).
   * La ingesta y las importaciones no lo necesitan.
   */
  acknowledgeDuplicate: z.boolean().optional(),
  /** G1: dejar el lead en la bolsa a propósito (sin aplicar el turno del proyecto). Solo sin `advisorId`. */
  keepInPool: z.boolean().optional(),
});
export type CreateLeadInput = z.infer<typeof createLeadSchema>;
/** Forma de entrada (antes de parsear): campos opcionales realmente opcionales. */
export type CreateLeadPayload = z.input<typeof createLeadSchema>;

/**
 * Edición parcial del lead: solo cambia lo que viene en el cuerpo. Los textos usan `optionalPatchText`
 * (no enviado = se conserva; vacío o null = se borra): editar el interés o el perfil nunca borra el
 * medio, el canal ni los UTM.
 */
export const updateLeadSchema = z
  .object({
    subStatus: z.string().min(1).max(40).optional(),
    interestLevel: z.nativeEnum(InterestLevel).optional(),
    score: z.number().int().min(0).max(100).optional(),
    scoreReason: optionalPatchText(300),
    medium: optionalPatchText(80),
    entryChannel: optionalPatchText(40),
    utmSource: optionalPatchText(120),
    utmMedium: optionalPatchText(120),
    utmCampaign: optionalPatchText(160),
    utmTerm: optionalPatchText(160),
    utmContent: optionalPatchText(160),
    externalIds: z.record(z.unknown()).optional(),
    /** Perfil de compra (editable en la ficha y por el bot). */
    profile: purchaseProfileSchema.optional(),
    /** Se fusiona con la atribución existente. */
    attribution: leadAttributionSchema.partial().optional(),
    /** Campos personalizados: se unen con los existentes; null borra la clave. */
    custom: customValuesInputSchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Nada que actualizar',
  });
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;
export type UpdateLeadPayload = z.input<typeof updateLeadSchema>;

export const assignLeadSchema = z.object({
  advisorId: z.string().uuid().nullable(),
  reason: optionalText(300),
});
export type AssignLeadInput = z.infer<typeof assignLeadSchema>;

export const bulkAssignSchema = z.object({
  leadIds: z.array(z.string().uuid()).min(1).max(BULK_ACTION_MAX_LEADS),
  advisorId: z.string().uuid().nullable(),
  reason: optionalText(300),
});

/**
 * Resultado de la reasignación masiva (G3): se valida todo antes de empezar y los que no se pudieron mover vuelven
 * con su motivo; los demás sí quedaron reasignados (nunca «a medias» sin decirlo). Lote 6 · I4 (P20): los que ya
 * tenían ese asesor (o ya estaban en la bolsa) no cuentan como reasignados ni se tocan: van en `unchanged`.
 */
export interface BulkAssignResult {
  assigned: number;
  /** Leads que ya tenían el asesor destino: sin cambios, sin avisos ni reencolado del embudo. */
  unchanged: number;
  failed: Array<{ id: string; reason: string }>;
}

export const desistLeadSchema = z.object({
  reason: z.string().min(1, 'Selecciona un motivo').max(40),
  comment: optionalText(1000),
});
export type DesistLeadInput = z.infer<typeof desistLeadSchema>;

/** Reabrir exige la siguiente tarea; es el mismo paso TASK de `nextStepSchema` (con la cita completa), `kind` opcional. */
export const reopenLeadSchema = z.object({
  comment: optionalText(1000),
  next: z.object({ kind: z.literal('TASK').default('TASK'), ...nextTaskFields }).optional(),
});
export type ReopenLeadInput = z.infer<typeof reopenLeadSchema>;
export type ReopenLeadPayload = z.input<typeof reopenLeadSchema>;

/**
 * G1: comprobación de identidad antes de registrar (misma regla que `createLead`: documento → celular → correo,
 * coincidencia exacta y normalizada). Solo se consultan los datos completos (documento de 8+, celular de 9, correo válido).
 */
export const checkIdentitySchema = z
  .object({
    companyId: z.string().uuid(),
    projectId: z.string().uuid(),
    documentNumber: optionalText(20),
    documentType: z.nativeEnum(DocumentType).optional().nullable(),
    phone: optionalText(60),
    email: optionalText(120),
  })
  .refine((v) => v.documentNumber || v.phone || v.email, {
    message: 'Indica documento, celular o correo',
    path: ['phone'],
  });
export type CheckIdentityInput = z.infer<typeof checkIdentitySchema>;
export type CheckIdentityPayload = z.input<typeof checkIdentitySchema>;

export interface IdentityLead {
  id: string;
  projectId: string;
  projectName: string;
  /** null = fuera del alcance de quien consulta (solo se dice el asesor y el proyecto). */
  status: LeadStatus | null;
  pipelineStage: PipelineStage | null;
  /** Etiqueta de la etapa para mostrar (nunca el código); null fuera del alcance. */
  pipelineStageLabel: string | null;
  advisorId: string | null;
  advisorName: string | null;
  /** false = fuera del alcance de quien consulta: solo se dice el asesor y el proyecto. */
  visible: boolean;
  /** Es el lead activo del proyecto elegido: ahí se anotaría el recontacto. */
  wouldReuse: boolean;
  /** null fuera del alcance. */
  capturedAt: string | null;
}

/** Una coincidencia de `POST /leads/check-identity` (G1): la persona, por qué dato coincidió y sus leads. */
export interface CheckIdentityMatch {
  contactId: string;
  /** Nombre completo; si quien consulta no ve ningún lead de la persona, solo nombre e iniciales. */
  name: string;
  /** Celular, correo y documento guardados: null cuando quien consulta no ve ningún lead de la persona. */
  phone: string | null;
  email: string | null;
  documentNumber: string | null;
  /** Por qué dato coincidió (`IdentityMatch` de `contacts.ts`: la misma clave que usa toda la identidad). */
  matchedOn: IdentityMatch;
  /** El dato con el que coincidió (el mismo que escribió quien consulta, normalizado), para que compare. */
  matchedValue: string;
  doNotContact: boolean;
  /** true = ningún lead de la persona está al alcance de quien consulta: los datos de contacto van ocultos. */
  restricted: boolean;
  leads: IdentityLead[];
}

export interface CheckIdentityResponse {
  /** Coincidencias en orden de confianza; la primera es la persona que `createLead` reutilizaría. */
  matches: CheckIdentityMatch[];
  /** Lead activo del proyecto elegido de la primera coincidencia (el recontacto se anota ahí), si existe. */
  wouldReuseLeadId: string | null;
  /** Ese lead lo atiende otro asesor y quien consulta no puede verlo: `createLead` no lo tocará (`outOfScope`). */
  outOfScope: boolean;
}

/** Cómo quedó el asesor del lead al registrarlo (G1): el indicado, el turno del proyecto, la bolsa o el que ya tenía. */
export type LeadAssignmentMode = 'INDICADO' | 'TURNO' | 'BOLSA' | 'CONSERVADO';

export interface CreateLeadResult {
  leadId: string;
  contactId: string;
  isRecontact: boolean;
  reusedExistingLead: boolean;
  projectName: string;
  advisorId: string | null;
  advisorName: string | null;
  assignment: LeadAssignmentMode;
  /** G1: la persona ya la atiende otro asesor en ese proyecto y quien registra no puede ver ese lead (no se tocó). */
  outOfScope: boolean;
  message: string | null;
  /** Datos del registro que no coincidían con los guardados y quedaron como alternos o anotados. */
  contactNotes: string[];
}

// ---- tipos de respuesta (no Zod: los produce la API)

export interface LeadListItem {
  id: string;
  companyId: string;
  projectId: string;
  projectName: string;
  contactId: string;
  contactName: string;
  /** Nombres y apellidos por separado (columnas de la tabla). */
  firstName: string;
  lastName: string | null;
  phone: string | null;
  phoneNormalized: string | null;
  email: string | null;
  /** false = el contacto pidió no ser contactado (`optOutAt`): no se le escribe ni se le llama. */
  contactable: boolean;
  optOutAt: string | null;
  capturedAt: string;
  medium: string | null;
  mediumCategory: string | null;
  originalMediumCategory: string | null;
  status: LeadStatus;
  subStatus: string | null;
  pipelineStage: PipelineStage;
  interestLevel: InterestLevel;
  score: number;
  advisorId: string | null;
  advisorName: string | null;
  followupState: (typeof FollowupStateValues)[number];
  overdueBand: (typeof OverdueBandValues)[number] | null;
  overdueDays: number;
  /** Gestiones completadas por personas (sin notas ni turnos del bot). */
  gestiones: number;
  /** 7.26: contacto del lead, intentos sin éxito y motivo del último intento fallido (código y etiqueta). */
  contactState: ContactState;
  contactAttempts: number;
  lastNonEffectiveReason: string | null;
  lastNonEffectiveReasonLabel: string | null;
  nextTaskAt: string | null;
  lastContactAt: string | null;
  isRecontact: boolean;
  /** G1: última vez que la persona volvió a registrarse sobre este lead (orden «Recontacto reciente»). */
  lastRecontactAt: string | null;
  pipelineStageChangedAt: string | null;
  /** Atención por bot (null = no lo atiende el bot). */
  attention: LeadAttention | null;
  tags: LeadTagDto[];
  custom: CustomValues;
}
